import { supabase } from '@/integrations/supabase/client';
import { verifyAndEnsureProfile, createOrUpdateUserProfile } from './profileService';

// Keep track of successful profile creations to prevent duplicates
const profileCreationAttempts = new Map<string, boolean>();

/**
 * Sign up a new user with Supabase Auth
 */
export const signUpUser = async (email: string, password: string, userData?: { name: string }) => {
  try {
    console.log("AUTH: Signing up user with data:", { email, userData });
    
    // Ensure session storage has default values if not set
    if (!sessionStorage.getItem('purchased_plan')) {
      sessionStorage.setItem('purchased_plan', 'basic');
    }
    
    if (!sessionStorage.getItem('purchased_credits')) {
      sessionStorage.setItem('purchased_credits', '1000');
    }
    
    // Create new auth account
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: userData?.name || '',
          // Include metadata to ensure RLS policies work correctly
          full_name: userData?.name || '',
          preferred_name: userData?.name || ''
        }
      }
    });
    
    if (error) {
      console.error("AUTH: Signup error:", error);
      throw error;
    }
    
    console.log("AUTH: Signup successful:", data);
    
    // Get plan or credits from session storage
    const purchasedPlan = sessionStorage.getItem('purchased_plan');
    const purchasedCredits = sessionStorage.getItem('purchased_credits');
    
    console.log("AUTH: Profile creation data:", { 
      email, 
      name: userData?.name,
      purchasedPlan,
      purchasedCredits 
    });
    
    // Ensure profile creation happens exactly once
    if (data.user && !profileCreationAttempts.has(email)) {
      // Mark that we're attempting profile creation for this email
      profileCreationAttempts.set(email, true);
      
      console.log(`AUTH: Profile creation attempt 1/1`);
      
      const profileCreated = await verifyAndEnsureProfile(
        email,
        userData?.name,
        purchasedPlan || undefined,
        purchasedCredits ? Number(purchasedCredits) : undefined
      );
      
      if (profileCreated) {
        console.log(`AUTH: Profile creation successful`);
      } else {
        console.error(`AUTH: Profile creation failed for ${email}`);
      }
      
      // ENHANCEMENT: Add explicit login after signup for consistent behavior
      console.log("AUTH: Performing explicit login after signup");
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (signInError) {
        console.error("AUTH: Explicit login after signup failed:", signInError);
      } else {
        console.log("AUTH: Explicit login after signup successful");
      }
    } else {
      console.log(`AUTH: Skipping profile creation - already attempted for ${email}`);
    }
    
    // Wait a bit longer to ensure all operations complete
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return { data, error: null };
  } catch (error) {
    console.error("AUTH: Error in signUpUser:", error);
    return { data: null, error };
  }
};

/**
 * Fallback function to create profile using a direct RPC call
 */
const createProfileFallback = async (
  email: string, 
  name?: string, 
  isSubscribed: boolean = false, 
  computePoints: number = 1000
) => {
  try {
    console.log("Attempting fallback profile creation for:", email);
    
    // Try direct insert with minimal data
    const { error } = await supabase
      .from('profiles')
      .insert({
        email: email,
        name: name || email.split('@')[0],
        is_subscribed: isSubscribed,
        compute_points: computePoints 
      });
      
    if (error) {
      console.error("Fallback profile creation error:", error);
      throw error;
    }
    
    console.log("Fallback profile creation successful");
  } catch (error) {
    console.error("Error in fallback profile creation:", error);
    throw error;
  }
};

/**
 * Get authentication session after signup
 */
export const getAuthSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error("Error getting session:", error);
      return { data: null, error };
    }
    return { data, error: null };
  } catch (error) {
    console.error("Error in getAuthSession:", error);
    return { data: null, error: error as Error };
  }
};

/**
 * Sign in user to get a token
 */
export const signInUser = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      console.error("Error signing in:", error);
      return { data: null, error };
    }
    
    return { data, error: null };
  } catch (error) {
    console.error("Error in signInUser:", error);
    return { data: null, error: error as Error };
  }
};
